import {
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
} from '@mui/material';
import { formatInTimeZone } from 'date-fns-tz';

import { colors } from '@client/new/components/colors';
import { usDollarFormatter } from '@client/new/helpers/currency-formatter';
import { Typography } from '@design-system/components/typography/typography';
import { sprinkles } from '@design-system/sprinkles.css';

import { HeaderTableCell } from './components/HeaderTableCell';
import { PaymentStatus } from './components/PaymentStatus';
import {
  EarningsBreakdown,
  SubscriptionPayouts,
} from './useSubscriptionPayouts';
import { DropDownFilter } from '@design-system/components/dropDown/dropDownFilter';

type RevenueTableRowProps = {
  name: string;
  revenue?: number;
  renderIfZero?: boolean;
  sx?: SxProps<Theme>;
};

const RevenueTableRow: React.FC<RevenueTableRowProps> = ({
  name,
  revenue,
  renderIfZero = false,
  sx,
}) => {
  if (revenue === undefined) {
    return null;
  }

  if (!renderIfZero && revenue === 0) {
    return null;
  }

  return (
    <TableRow sx={sx}>
      <TableCell align="left" className="p-4">
        <Typography>{name}</Typography>
      </TableCell>
      <TableCell align="right" className="p-4">
        <Typography>{usDollarFormatter.format(revenue)}</Typography>
      </TableCell>
    </TableRow>
  );
};

const BundleRevenueTableRow: React.FC<RevenueTableRowProps> = ({
  name,
  revenue,
  renderIfZero = false,
  sx,
}) => {
  if (revenue === undefined) {
    return null;
  }

  if (!renderIfZero && revenue === 0) {
    return null;
  }

  return (
    <TableRow sx={sx}>
      <TableCell align="left" className="p-4">
        <Typography className={sprinkles({ paddingLeft: '2xl' })}>
          {name}
        </Typography>
      </TableCell>
      <TableCell align="right" className="p-4">
        <Typography>{usDollarFormatter.format(revenue)}</Typography>
      </TableCell>
    </TableRow>
  );
};

type AnnualFutureSectionProps = {
  earnings: EarningsBreakdown;
};

const AnnualFutureSection: React.FC<AnnualFutureSectionProps> = ({
  earnings: {
    futureAnnualRevenue,
    futureRefundsAndDisputesRevenue,
    totalFutureRevenue,
    bundles,
  },
}) => {
  if (
    !futureAnnualRevenue &&
    !futureRefundsAndDisputesRevenue &&
    !totalFutureRevenue
  ) {
    return null;
  }

  return (
    <Table
      sx={{
        marginTop: 5,
        minWidth: 650,
        boxShadow:
          '0px 1px 2px 0px rgba(29, 41, 57, 0.08), 0px 0px 1px 0px #EAECF0',
        borderCollapse: 'collapse',
        borderRadius: '10px',
      }}
    >
      <TableHead
        sx={{
          backgroundColor: colors.secondary200,
        }}
      >
        <TableRow>
          <HeaderTableCell
            title="Future"
            align="left"
            tooltip="Annual subscriptions are paid out in 12 equal installments over 12 months. This number represents total revenue that you will make in future months from these installments. Future deductions are included here."
          />
          <HeaderTableCell title="Net Revenue" align="right" />
        </TableRow>
      </TableHead>
      <TableBody>
        <RevenueTableRow name="Annual (Future)" revenue={futureAnnualRevenue} />
        {bundles.map((bundle) => (
          <BundleRevenueTableRow
            key={bundle.id}
            name={bundle.title}
            revenue={bundle.futureAnnualRevenue}
          />
        ))}
        <RevenueTableRow
          name="Refunds & Disputes"
          revenue={futureRefundsAndDisputesRevenue}
          renderIfZero
        />
        {bundles.map((bundle) => (
          <BundleRevenueTableRow
            key={bundle.id}
            name={bundle.title}
            revenue={bundle.futureRefundsAndDisputesRevenue}
          />
        ))}
        <RevenueTableRow
          name="Total Future Payouts"
          revenue={totalFutureRevenue}
          sx={{ borderTop: '2px solid' }}
        />
        {bundles.map((bundle) => (
          <BundleRevenueTableRow
            key={bundle.id}
            name={bundle.title}
            revenue={bundle.totalFutureRevenue}
          />
        ))}
      </TableBody>
    </Table>
  );
};

export const SubscriptionRevenue: React.FC<SubscriptionPayouts> = (props) => {
  if (!props.hasPrograms && !props.bundles.length) {
    return null;
  }

  return (
    <div>
      <div className="flex gap-2 mb-5 items-center">
        <Typography variant="headline" size="small">
          Subscription Revenue
        </Typography>
        <PaymentStatus paidAt={props.paidAt} />
        {props.productFilter.show && (
          <DropDownFilter
            options={props.productFilter.options}
            onSelect={(value) => props.productFilter.onSelect(value)}
            value={props.productFilter.selectedValue}
            label={props.productFilter.label}
          />
        )}
      </div>
      <div className="flex flex-col gap-[30px]"></div>
      <Table
        sx={{
          minWidth: 650,
          boxShadow:
            '0px 1px 2px 0px rgba(29, 41, 57, 0.08), 0px 0px 1px 0px #EAECF0',
          borderCollapse: 'collapse',
          borderRadius: '10px',
        }}
      >
        <TableHead
          sx={{
            backgroundColor: colors.secondary200,
          }}
        >
          <TableRow>
            <HeaderTableCell
              title={formatInTimeZone(props.date, 'UTC', 'MMMM yyyy')}
              align="left"
            />
            <HeaderTableCell title="Net Revenue" align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          <RevenueTableRow
            name="Limited Monthly"
            revenue={props.limitedMonthlyRevenue}
          />
          <RevenueTableRow
            name="Limited Annual"
            revenue={props.limitedAnnualRevenue}
          />
          <RevenueTableRow name="Monthly" revenue={props.monthlyRevenue} />
          {props.bundles.map((bundle) => (
            <BundleRevenueTableRow
              key={bundle.id}
              name={bundle.title}
              revenue={bundle.monthlyRevenue}
            />
          ))}
          <RevenueTableRow name="Quarterly" revenue={props.quarterlyRevenue} />
          {props.bundles.map((bundle) => (
            <BundleRevenueTableRow
              key={bundle.id}
              name={bundle.title}
              revenue={bundle.quarterlyRevenue}
            />
          ))}
          <RevenueTableRow name="Annual" revenue={props.annualRevenue} />
          {props.bundles.map((bundle) => (
            <BundleRevenueTableRow
              key={bundle.id}
              name={bundle.title}
              revenue={bundle.annualRevenue}
            />
          ))}
          <RevenueTableRow
            name="Engagement"
            revenue={props.engagementRevenue}
          />
          {props.bundles.map((bundle) => (
            <BundleRevenueTableRow
              key={bundle.id}
              name={bundle.title}
              revenue={bundle.engagementRevenue}
            />
          ))}
          <RevenueTableRow
            name="Refunds & Disputes"
            revenue={props.refundsAndDisputesRevenue}
            renderIfZero
          />
          {props.bundles.map((bundle) => (
            <BundleRevenueTableRow
              key={bundle.id}
              name={bundle.title}
              revenue={bundle.refundsAndDisputesRevenue}
            />
          ))}
          <RevenueTableRow
            name="Total Subscription Revenue"
            revenue={props.totalSubscriptionRevenue}
            sx={{ borderTop: '2px solid' }}
            renderIfZero
          />
          {props.bundles.map((bundle) => (
            <BundleRevenueTableRow
              key={bundle.id}
              name={bundle.title}
              revenue={bundle.totalSubscriptionRevenue}
            />
          ))}
          {!props.productFilter.selectedValue && (
            <>
              <RevenueTableRow name="Subsidy" revenue={props.subsidy} />
              <RevenueTableRow
                name="Referral Deduction"
                revenue={props.referralDeduction ?? 0}
              />
              <RevenueTableRow
                name={props.payoutFeeLabel}
                revenue={props.payoutFee}
                renderIfZero
              />
              <RevenueTableRow
                name="Total Monthly Payout"
                revenue={props.totalPayout}
                sx={{ borderTop: '2px solid' }}
                renderIfZero
              />
              {props.totalPayout === 0 && (
                <TableRow>
                  <TableCell
                    align="left"
                    className="p-4 text-red-400"
                    colSpan={3}
                  >
                    <Typography>
                      Payouts are processed for earnings of{' '}
                      {usDollarFormatter.format(10)} or more.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </>
          )}
        </TableBody>
      </Table>
      <AnnualFutureSection earnings={props} />
    </div>
  );
};
